/**
 * Renders a markdown table in the chat transcript with the shared read-only
 * {@link MarkdownTableGrid} instead of a plain HTML `<table>`.
 *
 * Built from the hast `node` Streamdown passes (via `passNode`), not the
 * rendered React `children`: overriding `table` means its children are
 * Streamdown's own thead/tbody/td components (opaque functions), so their
 * structure can't be matched by tag. The hast tree, by contrast, is a
 * deterministic thead/tbody → tr → th/td shape, and each cell's inline content
 * (bold, italic, code, links) is converted straight to React.
 */

import React, { useMemo } from 'react';
import type { Element as HastElement, ElementContent } from 'hast';
import type {
  MarkdownTableCell,
  MarkdownTableData,
} from '@cotera/client/app/components/ui/data-grid/markdown-table';
import { ChatDatasetTable } from './chat-dataset-table';

const SAFE_HREF = /^(https?:|mailto:)/i;

const childElements = (node: HastElement, tagName: string): HastElement[] =>
  node.children.filter(
    (child): child is HastElement =>
      child.type === 'element' && child.tagName === tagName
  );

const firstChild = (
  node: HastElement | undefined,
  tagName: string
): HastElement | undefined =>
  node === undefined ? undefined : childElements(node, tagName)[0];

const hastToText = (nodes: ElementContent[]): string =>
  nodes
    .map((node) => {
      if (node.type === 'text') {
        return node.value;
      }
      if (node.type === 'element') {
        return hastToText(node.children);
      }
      return '';
    })
    .join('');

/** Convert a cell's inline hast content to React, keeping the common marks. */
const hastToReact = (nodes: ElementContent[]): React.ReactNode =>
  nodes.map((node, index) => {
    if (node.type === 'text') {
      return <React.Fragment key={index}>{node.value}</React.Fragment>;
    }
    if (node.type !== 'element') {
      return null;
    }
    const children = hastToReact(node.children);
    switch (node.tagName) {
      case 'strong':
      case 'b':
        return <strong key={index}>{children}</strong>;
      case 'em':
      case 'i':
        return <em key={index}>{children}</em>;
      case 'del':
        return <del key={index}>{children}</del>;
      case 'code':
        return (
          <code
            key={index}
            className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]"
          >
            {children}
          </code>
        );
      case 'br':
        return <br key={index} />;
      case 'a': {
        const href = node.properties?.['href'];
        if (typeof href === 'string' && SAFE_HREF.test(href)) {
          return (
            <a
              key={index}
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
            >
              {children}
            </a>
          );
        }
        return <React.Fragment key={index}>{children}</React.Fragment>;
      }
      default:
        return <React.Fragment key={index}>{children}</React.Fragment>;
    }
  });

const cellFrom = (cell: HastElement): MarkdownTableCell => ({
  text: hastToText(cell.children).trim(),
  content: hastToReact(cell.children),
});

/**
 * Rebuild {@link MarkdownTableData} from a `<table>` hast node. Returns null
 * when the node isn't a header-bearing GFM table so the caller can fall back to
 * default rendering.
 */
const tableDataFromNode = (
  node: HastElement | undefined
): MarkdownTableData | null => {
  const headerRow = firstChild(firstChild(node, 'thead'), 'tr');
  if (headerRow === undefined) {
    return null;
  }
  const headers = childElements(headerRow, 'th').map(cellFrom);
  if (headers.length === 0) {
    return null;
  }
  const tbody = firstChild(node, 'tbody');
  const bodyRows = tbody === undefined ? [] : childElements(tbody, 'tr');
  const rows = bodyRows.map((row) => childElements(row, 'td').map(cellFrom));
  return { headers, rows };
};

export const ResponseTable: React.FC<
  React.ComponentProps<'table'> & { node?: HastElement }
> = ({ node, children }) => {
  const data = useMemo(() => tableDataFromNode(node), [node]);

  if (data === null) {
    return <table>{children}</table>;
  }

  return <ChatDatasetTable data={data} />;
};
